import { computed, unref, type ComputedRef, type Ref } from "vue";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/vue-query";
import { buildScrollKey } from "@/utils/scrollKey";

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;
type QueryKey = readonly unknown[];

export interface InfiniteFeedListOptions<TPage, TItem, TPageParam> {
  queryKey: MaybeRef<QueryKey>;
  queryFn: (context: { pageParam: TPageParam }) => Promise<TPage>;
  getNextPageParam: (lastPage: TPage) => TPageParam | undefined;
  initialPageParam: TPageParam;
  selectItems: (page: TPage) => TItem[];
  enabled?: MaybeRef<boolean>;
  staleTime?: number;
  gcTime?: number;
  refetchOnMount?: boolean | "always";
  refetchOnWindowFocus?: boolean | "always";
  refetchOnReconnect?: boolean;
  retry?: number | boolean;
  retryDelay?: number | ((retryAttempt: number, error: Error) => number);
  scrollKeyPrefix?: string;
}

export interface InfiniteFeedListResult<TPage, TItem> {
  queryKey: ComputedRef<QueryKey>;
  scrollKey: ComputedRef<string>;
  items: ComputedRef<TItem[]>;
  data: Ref<InfiniteData<TPage> | undefined>;
  error: Ref<unknown>;
  isLoading: Ref<boolean>;
  isFetching: Ref<boolean>;
  isFetchingNextPage: Ref<boolean>;
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
  hasNextPage: Ref<boolean | undefined>;
  fetchNextPage: () => Promise<unknown>;
  refetch: () => Promise<unknown>;
}

export function useInfiniteFeedList<TPage, TItem, TPageParam>(
  options: InfiniteFeedListOptions<TPage, TItem, TPageParam>,
): InfiniteFeedListResult<TPage, TItem> {
  const resolvedQueryKey = computed(() => unref(options.queryKey));
  const enabled = computed(() =>
    options.enabled === undefined ? true : Boolean(unref(options.enabled)),
  );
  const scrollKey = computed(() =>
    buildScrollKey(options.scrollKeyPrefix ?? "query", resolvedQueryKey.value),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query = useInfiniteQuery<TPage, Error, InfiniteData<TPage>, QueryKey, TPageParam>({
    queryKey: resolvedQueryKey,
    queryFn: ({ pageParam }) =>
      options.queryFn({ pageParam: pageParam as TPageParam }),
    initialPageParam: options.initialPageParam as any,
    getNextPageParam: options.getNextPageParam,
    enabled,
    staleTime: options.staleTime,
    gcTime: options.gcTime,
    refetchOnMount: options.refetchOnMount,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchOnReconnect: options.refetchOnReconnect,
    retry: options.retry,
    retryDelay: options.retryDelay,
  });

  const items = computed<TItem[]>(() => {
    const pages = query.data.value?.pages ?? [];
    return pages.flatMap((page) => options.selectItems(page));
  });

  return {
    queryKey: resolvedQueryKey,
    scrollKey,
    items,
    data: query.data,
    error: query.error,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isSuccess: query.isSuccess,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}
